import { Box, ButtonBase, Stack, Typography, Dialog } from "@mui/material"
import { styled } from "@mui/material/styles"
import type { FC } from ".."
import { SvgIcon, ResponsiveImage } from ".."
import filmFestivalGlobals from "./assets/filmFestivalGlobals.json"
import { Transition, CloseButton } from "./JudgesSection"

type ConfirmationModalProps = {
  open: boolean
  onClose: () => void
}

const ConfirmationModal: FC<ConfirmationModalProps> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      sx={(theme) => ({
        "& .MuiDialog-paper": {
          backgroundColor: theme.palette["tertiary.800"].main,
          borderRadius: { xs: theme.spacing(2), md: theme.spacing(6) },
          maxWidth: 1094,
        },
      })}
    >
      <Box position="relative" padding={{ xs: 8, md: 12.5 }}>
        <Content />
        <CloseButton handleClose={onClose} />
      </Box>
    </Dialog>
  )
}

export default ConfirmationModal

// A sub component for the confirmation modal content
const Content: FC = () => {
  const { confirmationModal } = filmFestivalGlobals
  const image = {
    variants: [
      {
        width: 350,
        height: 470,
        url: confirmationModal.imageUrl,
        title: "",
        alt: "test",
        style: "default",
      },
    ],
  }

  return (
    <Box
      display="grid"
      gridTemplateColumns={{ xs: "1fr", md: "minmax(0, 1fr) 350px" }}
      gap={{ xs: 10, md: 30 }}
    >
      <Stack justifyContent="space-between" maxWidth={{ xs: "100%", md: 470 }}>
        <Typography
          variant="heading.md"
          sx={(theme) => ({
            color: theme.palette["primary.800"].main,
            fontWeight: 700,
            lineHeight: "90%!important",
            fontSize: "72px!important",
            [theme.breakpoints.down("md")]: {
              fontSize: "48px!important",
              marginBottom: 8,
            },
          })}
        >
          {confirmationModal.title}
          <span style={{ fontStyle: "italic", fontWeight: 400, display: "inline-flex" }}>
            {confirmationModal.keyword}
          </span>
        </Typography>
        <Stack
          sx={(theme) => ({
            "& span": {
              color: theme.palette["neutral.800"].main,
              fontSize: "18px!important",
              fontWeight: 600,
              letterSpacing: "-0.096px",
              lineHeight: "130%",
              display: "inline-flex",
            },
          })}
        >
          <Typography variant="body.md" mb={4}>
            {confirmationModal.description1}
          </Typography>
          <Box>
            <Typography variant="body.md">{confirmationModal.description2} </Typography>
            <StyledLink href={confirmationModal.redirectUrl} target="_blank">
              {confirmationModal.highlightedText}
            </StyledLink>
            <Typography variant="body.md">{confirmationModal.description3}</Typography>
          </Box>
        </Stack>
      </Stack>
      <Box
        sx={(theme) => ({
          position: "relative",
          borderRadius: theme.spacing(4),
          overflow: "hidden",
          maxWidth: { xs: "100%", md: "350px" },
          maxHeight: { xs: "100%", md: "470px" },
        })}
      >
        <ResponsiveImage
          image={image}
          sx={(theme) => ({
            maxWidth: { xs: "100%", md: "350px" },
            maxHeight: { xs: "100%", md: "470px" },
            aspectRatio: { xs: "1 / 1", md: "350 / 470" },
            borderRadius: theme.spacing(4),
          })}
        />
        <Box
          sx={(theme) => ({
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: theme.palette["primary.800"].main,
            mixBlendMode: "overlay",
            opacity: 0.4,
          })}
        />
        <ButtonBase
          onClick={() => {
            window.open(confirmationModal.redirectUrl, "_blank")
          }}
          sx={(theme) => ({
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            padding: theme.spacing(0, 6),
            minHeight: 49,
            backgroundColor: theme.palette["primary.800"].main,
            borderRadius: 999,
            color: theme.palette["neutral.800"].main,
            fontSize: "24px!important",
            fontWeight: 600,
            letterSpacing: "-0.096px",
          })}
        >
          <Typography
            variant="body.md"
            sx={(theme) => ({
              color: theme.palette["neutral.100"].main,
              fontWeight: 600,
              textTransform: "uppercase",
            })}
          >
            {confirmationModal.highlightedText}
          </Typography>
          <SvgIcon
            variant="arrowOutward"
            size={10}
            sx={{
              fill: (theme) => theme.palette["neutral.100"].main,
              marginLeft: 2,
            }}
          />
        </ButtonBase>
      </Box>
    </Box>
  )
}

const StyledLink = styled("a")(({ theme }) => ({
  color: theme.palette["primary.800"].main,
  fontWeight: 600,
  fontSize: "18px!important",
  lineHeight: "150%",
  textDecoration: "none",
  padding: theme.spacing(0, 1),
}))
